// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  createListenerMiddleware,
  type Action,
  type Dispatch,
  type ListenerEffectAPI,
} from '@reduxjs/toolkit';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { selectClient } from '@shared/data-access/slices/clients/clients-selectors';
import { clientsSlice } from '@shared/data-access/slices/clients/clients-slice';
import { type RootClientsState } from '@shared/data-access/slices/clients/clients-state';
import { AdvisoryProposalStatus } from '@shared/data-access/types/brokerage-types';
import { isClientApp } from '@shared/environment';
import { isDefined } from '@shared/utils';

const syncSelectedClientStatus = (
  api: ListenerEffectAPI<unknown, Dispatch<Action>>,
  clientId: number | null | undefined,
  status: AdvisoryProposalStatus | null | undefined,
) => {
  if (!isDefined(clientId) || !isDefined(status)) {
    return;
  }

  const selectedClient = selectClient(api.getState() as RootClientsState);

  if (selectedClient?.id !== clientId || selectedClient.status === status) {
    return;
  }

  api.dispatch(clientsSlice.actions.patchClientStatus({ id: clientId, status }));
};

export const clientSliceMiddleware = createListenerMiddleware();

clientSliceMiddleware.startListening({
  matcher: advisoryApi.endpoints.getAdvisoryUsers.matchFulfilled,
  effect: ({ payload }, { dispatch }) => {
    dispatch(clientsSlice.actions.setAdvisors(payload));
  },
});

clientSliceMiddleware.startListening({
  matcher: advisoryApi.endpoints.getAdvisoryInstitutions.matchFulfilled,
  effect: (action, { dispatch }) => {
    const client_id = action.meta.arg.originalArgs?.client_id;

    if (isDefined(client_id)) {
      dispatch(
        clientsSlice.actions.setInstitutions({
          client_id,
          institutions: action.payload,
        }),
      );

      return;
    }

    if (!isClientApp) {
      return;
    }

    dispatch(clientsSlice.actions.setClient({ id: 0, name: '', email: '' }));
    dispatch(
      clientsSlice.actions.setInstitutions({
        client_id: 0,
        institutions: action.payload,
      }),
    );
  },
});

clientSliceMiddleware.startListening({
  matcher: advisoryApi.endpoints.getClientProfile.matchFulfilled,
  effect: (action, api) => {
    const requestedClientId = action.meta.arg.originalArgs.client_id;

    if (action.payload.id !== requestedClientId) {
      return;
    }

    syncSelectedClientStatus(
      api,
      action.payload.id,
      action.payload.advisory_proposal?.status ?? AdvisoryProposalStatus.Inactive,
    );
  },
});

clientSliceMiddleware.startListening({
  matcher: advisoryApi.endpoints.getAdvisoryClients.matchFulfilled,
  effect: (action, api) => {
    const selectedClient = selectClient(api.getState() as RootClientsState);
    const listedClient = action.payload.data.find(
      client => client.id === selectedClient?.id,
    );

    syncSelectedClientStatus(api, listedClient?.id, listedClient?.status);
  },
});
